import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchAllUsers,
  updateUserRole,
  fetchAllPapers,
  createIssueWithPdf,
  updateIssueWithPdf,
  fetchIssues,
  fetchContactForms,
  markContactFormAsRead,
  fetchNewsletterSubscriptions,
  fetchAdminEditorialBoard,
  createEditorialBoardMember,
  updateEditorialBoardMember,
  deleteEditorialBoardMember,
  fetchAdminEditorialTeam,
  createEditorialTeamMember,
  updateEditorialTeamMember,
  deleteEditorialTeamMember,
  downloadIssuePdf
} from '../utils/api';
import { useAuth } from '../context/AuthContext';

const AdminDashboardEnhanced = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [papers, setPapers] = useState([]);
  const [issues, setIssues] = useState([]);
  const [contactForms, setContactForms] = useState([]);
  const [newsletterSubs, setNewsletterSubs] = useState([]);
  const [editorialBoard, setEditorialBoard] = useState([]);
  const [editorialTeam, setEditorialTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionResult, setActionResult] = useState({ type: null, message: null });

  // Form states
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [selectedBoardMember, setSelectedBoardMember] = useState(null);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [issueFile, setIssueFile] = useState(null);
  const [memberPhoto, setMemberPhoto] = useState(null);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);

      switch (activeTab) {
        case 'users':
          const userData = await fetchAllUsers();
          setUsers(Array.isArray(userData) ? userData : []);
          break;
        case 'papers':
          const papersData = await fetchAllPapers();
          setPapers(Array.isArray(papersData) ? papersData : []);
          break;
        case 'issues':
          const [papersData2, issuesData] = await Promise.all([
            fetchAllPapers(),
            fetchIssues()
          ]);
          setPapers(Array.isArray(papersData2) ? papersData2 : []);
          setIssues(Array.isArray(issuesData) ? issuesData : []);
          break;
        case 'contact':
          const formsData = await fetchContactForms();
          setContactForms(Array.isArray(formsData) ? formsData : []);
          break;
        case 'newsletter':
          const subsData = await fetchNewsletterSubscriptions();
          setNewsletterSubs(Array.isArray(subsData) ? subsData : []);
          break;
        case 'editorial-board':
          const boardData = await fetchAdminEditorialBoard();
          setEditorialBoard(Array.isArray(boardData) ? boardData : []);
          break;
        case 'editorial-team':
          const teamData = await fetchAdminEditorialTeam();
          setEditorialTeam(Array.isArray(teamData) ? teamData : []);
          break;
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    if (window.confirm(`Are you sure you want to change this user's role to ${newRole}?`)) {
      try {
        await updateUserRole(userId, newRole);
        setUsers(users.map(u =>
          u.id === userId ? { ...u, role: newRole } : u
        ));
        showMessage('success', 'User role updated successfully');
      } catch (err) {
        console.error('Error updating user role:', err);
        showMessage('error', 'Failed to update user role');
      }
    }
  };

  const handleMarkAsRead = async (contactId) => {
    try {
      await markContactFormAsRead(contactId);
      setContactForms(contactForms.map(c =>
        c.id === contactId ? { ...c, status: 'read' } : c
      ));
      showMessage('success', 'Contact form marked as read');
    } catch (err) {
      console.error('Error marking contact as read:', err);
      showMessage('error', 'Failed to update contact status');
    }
  };

  const handleDownloadIssuePdf = async (issueId) => {
    try {
      const blob = await downloadIssuePdf(issueId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `issue_${issueId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      showMessage('error', 'Failed to download PDF');
    }
  };

  const handleSubmitEditorialBoardMember = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    try {
      if (selectedBoardMember) {
        await updateEditorialBoardMember(selectedBoardMember.id, formData);
        showMessage('success', 'Editorial board member updated successfully');
      } else {
        await createEditorialBoardMember(formData);
        showMessage('success', 'Editorial board member added successfully');
      }
      setSelectedBoardMember(null);
      loadData();
    } catch (err) {
      console.error('Error saving editorial board member:', err);
      showMessage('error', 'Failed to save editorial board member');
    }
  };

  const handleDeleteEditorialBoardMember = async (memberId) => {
    if (window.confirm('Are you sure you want to delete this member?')) {
      try {
        await deleteEditorialBoardMember(memberId);
        setEditorialBoard(editorialBoard.filter(m => m.id !== memberId));
        showMessage('success', 'Editorial board member deleted successfully');
      } catch (err) {
        console.error('Error deleting member:', err);
        showMessage('error', 'Failed to delete member');
      }
    }
  };

  const showMessage = (type, message) => {
    setActionResult({ type, message });
    setTimeout(() => {
      setActionResult({ type: null, message: null });
    }, 5000);
  };

  const renderContactFormsTab = () => (
    <div className="card">
      <div className="card-header">
        <h3 className="mb-0">Contact Form Submissions</h3>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : contactForms.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Subject</th>
                  <th>Message</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contactForms.map(contact => (
                  <tr key={contact.id} className={contact.status === 'unread' ? 'table-light' : ''}>
                    <td>{contact.name}</td>
                    <td>{contact.email}</td>
                    <td>{contact.subject}</td>
                    <td className="text-truncate" style={{ maxWidth: '200px' }}>
                      {contact.message}
                    </td>
                    <td>{new Date(contact.submittedAt).toLocaleDateString()}</td>
                    <td>
                      <span className={`badge ${contact.status === 'unread' ? 'bg-warning' : 'bg-secondary'}`}>
                        {contact.status}
                      </span>
                    </td>
                    <td>
                      {contact.status === 'unread' && (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleMarkAsRead(contact.id)}
                        >
                          Mark as Read
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-4">
            <p>No contact form submissions found.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderNewsletterTab = () => (
    <div className="card">
      <div className="card-header">
        <h3 className="mb-0">Newsletter Subscriptions</h3>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : newsletterSubs.length > 0 ? (
          <>
            <div className="mb-3">
              <button className="btn btn-success">
                Export to CSV
              </button>
            </div>
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Subscribed Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {newsletterSubs.map(sub => (
                    <tr key={sub.id}>
                      <td>{sub.email}</td>
                      <td>{sub.name || '-'}</td>
                      <td>{new Date(sub.subscribedAt).toLocaleDateString()}</td>
                      <td>
                        <span className={`badge ${sub.isActive ? 'bg-success' : 'bg-secondary'}`}>
                          {sub.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <p>No newsletter subscriptions found.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderEditorialBoardTab = () => (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h3 className="mb-0">Editorial Board Management</h3>
        <button
          className="btn btn-primary"
          data-bs-toggle="modal"
          data-bs-target="#boardMemberModal"
          onClick={() => setSelectedBoardMember(null)}
        >
          Add New Member
        </button>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : editorialBoard.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Name</th>
                  <th>Title</th>
                  <th>Institution</th>
                  <th>Department</th>
                  <th>Email</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {editorialBoard.sort((a, b) => a.order - b.order).map(member => (
                  <tr key={member.id}>
                    <td>{member.order}</td>
                    <td>{member.name}</td>
                    <td>{member.title}</td>
                    <td>{member.institution}</td>
                    <td>{member.department}</td>
                    <td>{member.email}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        data-bs-toggle="modal"
                        data-bs-target="#boardMemberModal"
                        onClick={() => setSelectedBoardMember(member)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeleteEditorialBoardMember(member.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-4">
            <p>No editorial board members found.</p>
          </div>
        )}
      </div>

      {/* Editorial Board Member Modal */}
      <div className="modal fade" id="boardMemberModal" tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {selectedBoardMember ? 'Edit Board Member' : 'Add New Board Member'}
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <form onSubmit={handleSubmitEditorialBoardMember}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      defaultValue={selectedBoardMember?.name}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      className="form-control"
                      name="title"
                      defaultValue={selectedBoardMember?.title}
                      required
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Institution</label>
                    <input
                      type="text"
                      className="form-control"
                      name="institution"
                      defaultValue={selectedBoardMember?.institution}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Department</label>
                    <input
                      type="text"
                      className="form-control"
                      name="department"
                      defaultValue={selectedBoardMember?.department}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      defaultValue={selectedBoardMember?.email}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Order</label>
                    <input
                      type="number"
                      className="form-control"
                      name="order"
                      defaultValue={selectedBoardMember?.order || editorialBoard.length + 1}
                      min="1"
                      required
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Expertise</label>
                  <input
                    type="text"
                    className="form-control"
                    name="expertise"
                    defaultValue={selectedBoardMember?.expertise}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Bio</label>
                  <textarea
                    className="form-control"
                    name="bio"
                    rows="3"
                    defaultValue={selectedBoardMember?.bio}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label className="form-label">Photo</label>
                  <input
                    type="file"
                    className="form-control"
                    name="photo"
                    accept="image/*"
                    onChange={(e) => setMemberPhoto(e.target.files[0])}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {selectedBoardMember ? 'Update' : 'Add'} Member
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEnhancedIssuesTab = () => (
    <div className="card">
      <div className="card-header">
        <h3 className="mb-0">Journal Issues with PDF Management</h3>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className="table-responsive mb-4">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Issue Title</th>
                    <th>Volume / Issue</th>
                    <th>Publication Date</th>
                    <th>Papers</th>
                    <th>PDF</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {issues.map(issue => (
                    <tr key={issue.id}>
                      <td>{issue.title}</td>
                      <td>Vol. {issue.volume}, No. {issue.issue}</td>
                      <td>{new Date(issue.publicationDate).toLocaleDateString()}</td>
                      <td>{issue.papers?.length || 0}</td>
                      <td>
                        {issue.pdfPath ? (
                          <span className="badge bg-success">Available</span>
                        ) : (
                          <span className="badge bg-secondary">Not uploaded</span>
                        )}
                      </td>
                      <td>
                        {issue.pdfPath && (
                          <button
                            className="btn btn-sm btn-outline-success me-2"
                            onClick={() => handleDownloadIssuePdf(issue.id)}
                          >
                            Download PDF
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setSelectedIssue(issue)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="card">
              <div className="card-header">
                <h4>{selectedIssue ? 'Edit Issue' : 'Create New Issue'}</h4>
              </div>
              <div className="card-body">
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  if (issueFile) {
                    formData.append('issuePdf', issueFile);
                  }
                  if (selectedIssue) {
                    updateIssueWithPdf(selectedIssue.id, formData).then(() => {
                      showMessage('success', 'Issue updated successfully');
                      loadData();
                    });
                  } else {
                    createIssueWithPdf(formData).then(() => {
                      showMessage('success', 'Issue created successfully');
                      loadData();
                    });
                  }
                }}>
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <label className="form-label">Issue Title</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        defaultValue={selectedIssue?.title}
                        required
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label">Volume</label>
                      <input
                        type="number"
                        className="form-control"
                        name="volume"
                        defaultValue={selectedIssue?.volume}
                        required
                        min="1"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Issue Number</label>
                      <input
                        type="number"
                        className="form-control"
                        name="issue"
                        defaultValue={selectedIssue?.issue}
                        required
                        min="1"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Year</label>
                      <input
                        type="number"
                        className="form-control"
                        name="year"
                        defaultValue={selectedIssue?.year || new Date().getFullYear()}
                        required
                        min="2000"
                        max="2100"
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Month</label>
                      <select className="form-control" name="month" defaultValue={selectedIssue?.month}>
                        <option value="January">January</option>
                        <option value="February">February</option>
                        <option value="March">March</option>
                        <option value="April">April</option>
                        <option value="May">May</option>
                        <option value="June">June</option>
                        <option value="July">July</option>
                        <option value="August">August</option>
                        <option value="September">September</option>
                        <option value="October">October</option>
                        <option value="November">November</option>
                        <option value="December">December</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Publication Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="publicationDate"
                        defaultValue={selectedIssue?.publicationDate}
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      rows="3"
                      defaultValue={selectedIssue?.description}
                    ></textarea>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Upload Issue PDF</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="application/pdf"
                      onChange={(e) => setIssueFile(e.target.files[0])}
                    />
                    {selectedIssue?.pdfPath && (
                      <small className="text-muted">Current PDF available. Upload new to replace.</small>
                    )}
                  </div>

                  <div className="d-flex justify-content-end">
                    {selectedIssue && (
                      <button
                        type="button"
                        className="btn btn-secondary me-2"
                        onClick={() => setSelectedIssue(null)}
                      >
                        Cancel
                      </button>
                    )}
                    <button type="submit" className="btn btn-primary">
                      {selectedIssue ? 'Update Issue' : 'Create Issue'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderPapersTab = () => (
    <div className="card">
      <div className="card-header">
        <h3 className="mb-0">All Papers</h3>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : papers.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Authors</th>
                  <th>Submission Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {papers.map(paper => (
                  <tr key={paper.id}>
                    <td>{paper.title}</td>
                    <td>{paper.authors?.map(a => typeof a === 'object' ? a.name : a).join(', ')}</td>
                    <td>{new Date(paper.submittedAt || paper.submissionDate).toLocaleDateString()}</td>
                    <td>
                      <span className={`badge ${
                        paper.status === 'PUBLISHED' ? 'bg-success' :
                        paper.status === 'ACCEPTED' ? 'bg-success' :
                        paper.status === 'REJECTED' ? 'bg-danger' :
                        paper.status === 'UNDER_REVIEW' ? 'bg-primary' :
                        paper.status === 'REVISIONS_REQUIRED' ? 'bg-warning' :
                        'bg-info'
                      }`}>
                        {paper.status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary">
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-4">
            <p>No papers found.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderEditorialTeamTab = () => (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h3 className="mb-0">Editorial Team Management</h3>
        <button
          className="btn btn-primary"
          onClick={() => alert('Add team member functionality coming soon')}
        >
          Add New Member
        </button>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : editorialTeam.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Institution</th>
                  <th>Email</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {editorialTeam.sort((a, b) => a.order - b.order).map(member => (
                  <tr key={member.id}>
                    <td>{member.order}</td>
                    <td>{member.name}</td>
                    <td>{member.role}</td>
                    <td>{member.institution}</td>
                    <td>{member.email}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => alert('Edit functionality coming soon')}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => alert('Delete functionality coming soon')}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-4">
            <p>No editorial team members found.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderUsersTab = () => (
    <div className="card">
      <div className="card-header">
        <h3 className="mb-0">Manage Users</h3>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : users.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Institution</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>{user.firstName} {user.lastName}</td>
                    <td>{user.email}</td>
                    <td>{user.institution || '-'}</td>
                    <td>
                      <span className={`badge ${
                        user.role === 'ADMIN' ? 'bg-danger' :
                        user.role === 'EDITOR' ? 'bg-warning' :
                        'bg-info'
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td>
                      <div className="dropdown">
                        <button
                          className="btn btn-sm btn-outline-secondary dropdown-toggle"
                          type="button"
                          data-bs-toggle="dropdown"
                        >
                          Change Role
                        </button>
                        <ul className="dropdown-menu">
                          <li>
                            <button
                              className="dropdown-item"
                              onClick={() => handleUpdateUserRole(user.id, 'AUTHOR')}
                              disabled={user.role === 'AUTHOR'}
                            >
                              Author
                            </button>
                          </li>
                          <li>
                            <button
                              className="dropdown-item"
                              onClick={() => handleUpdateUserRole(user.id, 'EDITOR')}
                              disabled={user.role === 'EDITOR'}
                            >
                              Editor
                            </button>
                          </li>
                          <li>
                            <button
                              className="dropdown-item"
                              onClick={() => handleUpdateUserRole(user.id, 'ADMIN')}
                              disabled={user.role === 'ADMIN'}
                            >
                              Admin
                            </button>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-4">
            <p>No users found.</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="container py-5">
      <div className="row mb-4">
        <div className="col">
          <h1>Enhanced Admin Dashboard</h1>
          <p className="lead">
            Welcome, {user?.firstName} {user?.lastName}. Manage all aspects of the journal system here.
          </p>
        </div>
      </div>

      {actionResult.message && (
        <div className={`alert ${actionResult.type === 'success' ? 'alert-success' : 'alert-danger'}`} role="alert">
          {actionResult.message}
        </div>
      )}

      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Users
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'papers' ? 'active' : ''}`}
            onClick={() => setActiveTab('papers')}
          >
            Papers
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'issues' ? 'active' : ''}`}
            onClick={() => setActiveTab('issues')}
          >
            Issues & PDFs
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'editorial-board' ? 'active' : ''}`}
            onClick={() => setActiveTab('editorial-board')}
          >
            Editorial Board
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'editorial-team' ? 'active' : ''}`}
            onClick={() => setActiveTab('editorial-team')}
          >
            Editorial Team
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'contact' ? 'active' : ''}`}
            onClick={() => setActiveTab('contact')}
          >
            Contact Forms
            {contactForms.filter(c => c.status === 'unread').length > 0 && (
              <span className="badge bg-danger ms-2">
                {contactForms.filter(c => c.status === 'unread').length}
              </span>
            )}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'newsletter' ? 'active' : ''}`}
            onClick={() => setActiveTab('newsletter')}
          >
            Newsletter
          </button>
        </li>
      </ul>

      {activeTab === 'users' && renderUsersTab()}
      {activeTab === 'papers' && renderPapersTab()}
      {activeTab === 'issues' && renderEnhancedIssuesTab()}
      {activeTab === 'editorial-board' && renderEditorialBoardTab()}
      {activeTab === 'editorial-team' && renderEditorialTeamTab()}
      {activeTab === 'contact' && renderContactFormsTab()}
      {activeTab === 'newsletter' && renderNewsletterTab()}
    </div>
  );
};

export default AdminDashboardEnhanced;